// @ts-nocheck
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { hasPermission, type UserRole } from '@product-radar/shared';
import { formatDate } from '@/lib/formatting';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetBody, SheetFooter } from './ui/sheet';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { 
  Plus, 
  Edit, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  Layers,
  FileText,
  Zap,
  MousePointer,
  History,
  Loader2,
  Package,
  Lock
} from 'lucide-react';

interface Product {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ProductStructureItem {
  id: string;
  workspace_id: string;
  product_id: string | null;
  parent_id: string | null;
  type: 'module' | 'page' | 'feature' | 'element';
  name: string;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  children?: ProductStructureItem[];
}

interface ChangelogEntry {
  id: string;
  action: 'created' | 'updated' | 'deleted';
  item_type: string;
  item_name: string;
  changed_by: string;
  changed_at: string;
  profiles?: {
    full_name: string;
  };
}

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'product': return <Package className="h-4 w-4" />;
    case 'module': return <Layers className="h-4 w-4" />;
    case 'page': return <FileText className="h-4 w-4" />;
    case 'feature': return <Zap className="h-4 w-4" />;
    case 'element': return <MousePointer className="h-4 w-4" />;
    default: return <FileText className="h-4 w-4" />;
  }
};

const getTypeBadge = (type: string) => {
  switch (type) {
    case 'product': return 'default';
    case 'module': return 'destructive';
    case 'page': return 'default';
    case 'feature': return 'secondary';
    case 'element': return 'outline';
    default: return 'outline';
  }
};

export function ProductDefinition() {
  const { workspace, membership } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const userRole = membership?.role as UserRole;
  const isAdmin = userRole === 'admin';
  
  const [products, setProducts] = useState<Product[]>([]);
  const [items, setItems] = useState<ProductStructureItem[]>([]);
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set<string>());
  const [expandedProducts, setExpandedProducts] = useState(new Set<string>());
  const [selectedItem, setSelectedItem] = useState<ProductStructureItem | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // Dialog states
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addChildDialogOpen, setAddChildDialogOpen] = useState(false);
  const [parentForNewItem, setParentForNewItem] = useState<ProductStructureItem | null>(null);
  const [productForNewModule, setProductForNewModule] = useState<string>('');
  
  // Form states
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState('#245F73');
  const [formType, setFormType] = useState<'module' | 'page' | 'feature' | 'element'>('module');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (workspace) {
      loadProducts();
      loadProductStructure();
      loadChangelog();
    }
  }, [workspace]);

  const loadProducts = async () => {
    if (!workspace) return;

    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('workspace_id', workspace.id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        console.error('Error loading products:', error);
        toast.error('Failed to load products');
        return;
      }

      setProducts(data || []);
      
      // Auto-expand all products
      if (data && data.length > 0) {
        setExpandedProducts(new Set(data.map(p => p.id)));
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    }
  };

  const loadProductStructure = async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('product_structure_items')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading product structure:', error);
        toast.error('Failed to load product structure');
        return;
      }

      // Build hierarchical structure per product
      const hierarchical = buildHierarchy(data || []);
      setItems(hierarchical);
    } catch (error) {
      console.error('Error loading product structure:', error);
      toast.error('Failed to load product structure');
    } finally {
      setLoading(false);
    }
  };

  const loadChangelog = async () => {
    if (!workspace) return;

    try {
      const { data, error } = await supabase
        .from('product_structure_changelog')
        .select(`
          id,
          action,
          item_type,
          item_name,
          changed_by,
          changed_at,
          profiles:changed_by (
            full_name
          )
        `)
        .eq('workspace_id', workspace.id)
        .order('changed_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        setChangelog(data as any);
      }
    } catch (error) {
      console.error('Error loading changelog:', error);
    }
  };

  const buildHierarchy = (flatItems: any[]): ProductStructureItem[] => {
    const itemMap = new Map();
    const roots: ProductStructureItem[] = [];

    // First pass: create map of all items
    flatItems.forEach(item => {
      itemMap.set(item.id, { ...item, children: [] });
    });

    // Second pass: build hierarchy
    flatItems.forEach(item => {
      const node = itemMap.get(item.id);
      if (item.parent_id) {
        const parent = itemMap.get(item.parent_id);
        if (parent) {
          parent.children.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const logChange = async (action: 'created' | 'updated' | 'deleted', itemType: string, itemName: string, itemId?: string) => {
    if (!workspace || !membership) return;

    await supabase
      .from('product_structure_changelog')
      .insert({
        workspace_id: workspace.id,
        product_item_id: itemId || null,
        action,
        item_type: itemType,
        item_name: itemName,
        changed_by: membership.user_id,
      });
  };

  // ============= PRODUCT CRUD =============
  const handleCreateProduct = async () => {
    if (!formName.trim() || !workspace || !membership) {
      toast.error('Please enter a product name');
      return;
    }

    setIsSaving(true);

    try {
      const { data, error } = await supabase
        .from('products')
        .insert({
          workspace_id: workspace.id,
          name: formName,
          description: formDescription || null,
          color: formColor,
          created_by: membership.user_id,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating product:', error);
        toast.error('Failed to create product');
        return;
      }

      await logChange('created', 'product', formName, data.id);
      
      toast.success('Product created successfully!');
      
      setAddProductDialogOpen(false);
      setFormName('');
      setFormDescription('');
      setFormColor('#245F73');
      
      loadProducts();
      loadChangelog();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Failed to create product');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct || !formName.trim() || !membership) {
      toast.error('Please enter a product name');
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('products')
        .update({
          name: formName,
          description: formDescription || null,
          color: formColor,
          updated_by: membership.user_id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedProduct.id);

      if (error) {
        console.error('Error updating product:', error);
        toast.error('Failed to update product');
        return;
      }

      await logChange('updated', 'product', formName, selectedProduct.id);

      toast.success('Product updated successfully!');
      
      setEditProductDialogOpen(false);
      setSelectedProduct(null);
      setFormName('');
      setFormDescription('');
      setFormColor('#245F73');
      
      loadProducts();
      loadChangelog();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    try {
      // Check if product has modules
      const { data: modules } = await supabase
        .from('product_structure_items')
        .select('id')
        .eq('product_id', product.id)
        .eq('type', 'module');

      if (modules && modules.length > 0) {
        toast.error('Cannot delete product with modules. Please delete all modules first.');
        return;
      }

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', product.id);

      if (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
        return;
      }

      await logChange('deleted', 'product', product.name);

      toast.success('Product deleted successfully');
      
      loadProducts();
      loadChangelog();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const openEditProductDialog = (product: Product) => {
    setSelectedProduct(product);
    setFormName(product.name);
    setFormDescription(product.description || '');
    setFormColor(product.color);
    setEditProductDialogOpen(true);
  };

  // ============= STRUCTURE ITEM CRUD =============
  const handleCreate = async () => {
    if (!formName.trim() || !workspace || !membership) {
      toast.error('Please enter a name');
      return;
    }

    // For modules, require product selection
    if (!parentForNewItem && !productForNewModule) {
      toast.error('Please select a product for this module');
      return;
    }

    setIsSaving(true);

    try {
      const { data, error } = await supabase
        .from('product_structure_items')
        .insert({
          workspace_id: workspace.id,
          parent_id: parentForNewItem?.id || null,
          product_id: parentForNewItem?.product_id || productForNewModule,
          type: formType,
          name: formName,
          description: formDescription || null,
          created_by: membership.user_id,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating item:', error);
        toast.error('Failed to create item');
        return;
      }

      await logChange('created', formType, formName, data.id);
      
      toast.success(`${formType.charAt(0).toUpperCase() + formType.slice(1)} created successfully!`);
      
      setAddDialogOpen(false);
      setAddChildDialogOpen(false);
      setFormName('');
      setFormDescription('');
      setFormType('module');
      setParentForNewItem(null);
      setProductForNewModule('');
      
      loadProductStructure();
      loadChangelog();
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error('Failed to create item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedItem || !formName.trim() || !membership) {
      toast.error('Please enter a name');
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('product_structure_items')
        .update({
          name: formName,
          description: formDescription || null,
          updated_by: membership.user_id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedItem.id);

      if (error) {
        console.error('Error updating item:', error);
        toast.error('Failed to update item');
        return;
      }

      await logChange('updated', selectedItem.type, formName, selectedItem.id);

      toast.success('Changes saved successfully!');
      
      setEditDialogOpen(false);
      setSelectedItem(null);
      setFormName('');
      setFormDescription('');
      
      loadProductStructure();
      loadChangelog();
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (item: ProductStructureItem) => {
    try {
      // Check if item has children
      const { data: children } = await supabase
        .from('product_structure_items')
        .select('id')
        .eq('parent_id', item.id);

      if (children && children.length > 0) {
        toast.error('Cannot delete item with children. Please delete children first.');
        return;
      }

      const { error } = await supabase
        .from('product_structure_items')
        .delete()
        .eq('id', item.id);

      if (error) {
        console.error('Error deleting item:', error);
        toast.error('Failed to delete item');
        return;
      }

      await logChange('deleted', item.type, item.name);

      toast.success(`${item.type.charAt(0).toUpperCase() + item.type.slice(1)} deleted successfully`);
      
      loadProductStructure();
      loadChangelog();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const toggleProductExpanded = (id: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedProducts(newExpanded);
  };

  const openEditDialog = (item: ProductStructureItem) => {
    setSelectedItem(item);
    setFormName(item.name);
    setFormDescription(item.description || '');
    setEditDialogOpen(true);
  };

  const openAddChildDialog = (parent: ProductStructureItem) => {
    setParentForNewItem(parent);
    setFormName('');
    setFormDescription('');
    
    // Set appropriate type based on parent
    if (parent.type === 'module') setFormType('page');
    else if (parent.type === 'page') setFormType('feature');
    else if (parent.type === 'feature') setFormType('element');
    else setFormType('element');
    
    setAddChildDialogOpen(true);
  };

  const openAddModuleDialog = (productId: string) => {
    setProductForNewModule(productId);
    setFormName('');
    setFormDescription('');
    setFormType('module');
    setParentForNewItem(null);
    setAddDialogOpen(true);
  };

  const renderProductItem = (item: ProductStructureItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const paddingLeft = level * 24;

    return (
      <div key={item.id}>
        <div 
          className={`flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50`}
          style={{ marginLeft: paddingLeft }}
        >
          <div className="flex items-center space-x-3 flex-1">
            {hasChildren && (
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(item.id);
                }}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            )}
            {!hasChildren && <div className="w-6" />}
            
            <div className="flex items-center space-x-2">
              {getTypeIcon(item.type)}
              <Badge variant={getTypeBadge(item.type)}>{item.type}</Badge>
            </div>
            
            <div className="flex-1">
              <div className="font-medium">{item.name}</div>
              {item.description && (
                <div className="text-sm text-muted-foreground">{item.description}</div>
              )}
            </div>
          </div>
          
          {hasPermission(userRole, 'canManageProductStructure') && (
            <div className="flex items-center space-x-2">
              {item.type !== 'element' && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => openAddChildDialog(item)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}>
                <Edit className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {item.type}</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{item.name}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => handleDelete(item)}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-2 space-y-2">
            {item.children!.map((child) => renderProductItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const countByType = (items: ProductStructureItem[], type: string): number => {
    let count = 0;
    items.forEach(item => {
      if (item.type === type) count++;
      if (item.children) {
        count += countByType(item.children, type);
      }
    });
    return count;
  };

  const formatChangelogDate = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(d, regionalSettings);
  };

  const getChangelogBadgeVariant = (action: string) => {
    switch (action) {
      case 'created': return 'outline';
      case 'updated': return 'secondary';
      case 'deleted': return 'destructive';
      default: return 'outline';
    }
  };

  const getChangelogDotColor = (action: string) => {
    switch (action) {
      case 'created': return 'bg-green-500';
      case 'updated': return 'bg-blue-500';
      case 'deleted': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#245F73]" />
      </div>
    );
  }

  const stats = {
    products: products.length,
    modules: countByType(items, 'module'),
    pages: countByType(items, 'page'),
    features: countByType(items, 'feature'),
    elements: countByType(items, 'element'),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1>Product Definition</h1>
          <p className="text-muted-foreground">Define your product structure for accurate feedback mapping</p>
        </div>
        {isAdmin && (
          <Sheet open={addProductDialogOpen} onOpenChange={setAddProductDialogOpen}>
            <SheetTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Add New Product</SheetTitle>
                <SheetDescription>Create a new product in your workspace</SheetDescription>
              </SheetHeader>
              
              <SheetBody>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="productName">Product Name</Label>
                    <Input 
                      id="productName" 
                      placeholder="e.g. Core Platform"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      disabled={isSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="productDescription">Description</Label>
                    <Textarea 
                      id="productDescription" 
                      placeholder="Describe what this product is..."
                      rows={3}
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      disabled={isSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="productColor">Color</Label>
                    <Input 
                      id="productColor" 
                      type="color"
                      value={formColor}
                      onChange={(e) => setFormColor(e.target.value)}
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </SheetBody>
              
              <SheetFooter>
                <Button variant="outline" onClick={() => setAddProductDialogOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleCreateProduct} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Product'
                  )}
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.products}</div>
            <div className="text-xs text-muted-foreground">Top-level products</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Modules</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.modules}</div>
            <div className="text-xs text-muted-foreground">Product modules</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pages}</div>
            <div className="text-xs text-muted-foreground">Total pages</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.features}</div>
            <div className="text-xs text-muted-foreground">Defined features</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Elements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.elements}</div>
            <div className="text-xs text-muted-foreground">UI elements</div>
          </CardContent>
        </Card>
      </div>

      {/* Products List */}
      {products.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No products defined yet</p>
              {isAdmin && (
                <Button onClick={() => setAddProductDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Product
                </Button>
              )}
              {!isAdmin && (
                <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                  <Lock className="h-4 w-4" />
                  <span>Only admins can create products</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {products.map(product => {
            const productModules = items.filter(item => item.product_id === product.id && !item.parent_id);
            const isExpanded = expandedProducts.has(product.id);
            
            return (
              <Card key={product.id} style={{ borderLeft: `4px solid ${product.color}` }}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 h-8 w-8"
                        onClick={() => toggleProductExpanded(product.id)}
                      >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </Button>
                      <Package className="h-5 w-5" style={{ color: product.color }} />
                      <div className="flex-1">
                        <CardTitle>{product.name}</CardTitle>
                        {product.description && (
                          <CardDescription>{product.description}</CardDescription>
                        )}
                      </div>
                      <Badge variant="outline">{productModules.length} modules</Badge>
                    </div>
                    
                    {isAdmin && (
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openAddModuleDialog(product.id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Module
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditProductDialog(product)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Product</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{product.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleDeleteProduct(product)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent>
                    {productModules.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No modules in this product yet</p>
                        {hasPermission(userRole, 'canManageProductStructure') && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-3"
                            onClick={() => openAddModuleDialog(product.id)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Module
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {productModules.map((item) => renderProductItem(item))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent Changes */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Changes</CardTitle>
          <CardDescription>Track changes to your product definition</CardDescription>
        </CardHeader>
        <CardContent>
          {changelog.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No changes yet</p>
          ) : (
            <div className="space-y-3">
              {changelog.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${getChangelogDotColor(entry.action)}`}></div>
                    <div>
                      <div className="font-medium">
                        {entry.action.charAt(0).toUpperCase() + entry.action.slice(1)} "{entry.item_name}"
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatChangelogDate(entry.changed_at)} • {(entry.profiles as any)?.full_name || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <Badge variant={getChangelogBadgeVariant(entry.action)}>
                    {entry.action}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Product Dialog */}
      <Sheet open={editProductDialogOpen} onOpenChange={setEditProductDialogOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Product</SheetTitle>
            <SheetDescription>Update the product details</SheetDescription>
          </SheetHeader>
          
          <SheetBody>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="editProductName">Name</Label>
                <Input 
                  id="editProductName" 
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editProductDescription">Description</Label>
                <Textarea 
                  id="editProductDescription" 
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editProductColor">Color</Label>
                <Input 
                  id="editProductColor" 
                  type="color"
                  value={formColor}
                  onChange={(e) => setFormColor(e.target.value)}
                  disabled={isSaving}
                />
              </div>
            </div>
          </SheetBody>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditProductDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProduct} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Structure Item Dialog */}
      <Sheet open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit {selectedItem?.type}</SheetTitle>
            <SheetDescription>Update the {selectedItem?.type} details</SheetDescription>
          </SheetHeader>
          
          <SheetBody>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="editName">Name</Label>
                <Input 
                  id="editName" 
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editDescription">Description</Label>
                <Textarea 
                  id="editDescription" 
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                  disabled={isSaving}
                />
              </div>
            </div>
          </SheetBody>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Add Module Dialog */}
      <Sheet open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add New Module</SheetTitle>
            <SheetDescription>Create a new module in this product</SheetDescription>
          </SheetHeader>
          
          <SheetBody>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="moduleName">Module Name</Label>
                <Input 
                  id="moduleName" 
                  placeholder="e.g. User Management"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="moduleDescription">Description</Label>
                <Textarea 
                  id="moduleDescription" 
                  placeholder="Describe what this module covers..."
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  disabled={isSaving}
                />
              </div>
            </div>
          </SheetBody>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Module'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Add Child Dialog */}
      <Sheet open={addChildDialogOpen} onOpenChange={setAddChildDialogOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add {formType}</SheetTitle>
            <SheetDescription>
              Add a new {formType} under "{parentForNewItem?.name}"
            </SheetDescription>
          </SheetHeader>
          
          <SheetBody>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="childName">Name</Label>
                <Input 
                  id="childName" 
                  placeholder={`e.g. My ${formType}`}
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="childDescription">Description</Label>
                <Textarea 
                  id="childDescription" 
                  placeholder="Describe this item..."
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  disabled={isSaving}
                />
              </div>
            </div>
          </SheetBody>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setAddChildDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                `Create ${formType}`
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
